import { Button } from '@cloudflare/kumo';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { z } from 'zod';
import type { billingPaymentSchema } from '../../shared/billing.ts';
import { api, queryClient, unwrap } from '../api.ts';
import { ErrorNotice, Loading } from '../common.tsx';

type PaymentActions = {
  requireSubmission?: boolean;
  submitLabel?: string;
  disabled?: boolean;
  onPendingChange?: (pending: boolean) => void;
};

type Payment = NonNullable<z.infer<typeof billingPaymentSchema>>;
export function billingPaymentQuery(accountId: string, operationId?: string, invoiceId?: string) {
  return queryOptions({
    queryKey: ['billing-payment', accountId, operationId, invoiceId],
    queryFn: async () =>
      operationId
        ? unwrap(
            await api.GET('/api/accounts/{accountUserId}/billing/{operationId}/payment', {
              params: { path: { accountUserId: accountId, operationId } },
            }),
          )
        : unwrap(
            await api.GET('/api/accounts/{accountUserId}/invoices/{invoiceId}/payment', {
              params: { path: { accountUserId: accountId, invoiceId: invoiceId! } },
            }),
          ),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });
}

export function BillingPayment({
  accountId,
  operationId,
  invoiceId,
  onComplete,
  ...actions
}: {
  accountId: string;
  operationId?: string;
  invoiceId?: string;
  onComplete?: () => void;
} & PaymentActions) {
  const [completed, setCompleted] = useState(false);
  const finished = useRef(false);
  const complete = useRef(onComplete);
  useEffect(() => {
    complete.current = onComplete;
  }, [onComplete]);
  const payment = useQuery(billingPaymentQuery(accountId, operationId, invoiceId));
  const finish = useCallback(() => {
    if (finished.current) return;
    finished.current = true;
    setCompleted(true);
    complete.current?.();
    void queryClient.invalidateQueries({ queryKey: ['billing', accountId] });
    void queryClient.invalidateQueries({ queryKey: ['invoices', accountId] });
  }, [accountId]);
  useEffect(() => {
    if (payment.isSuccess && payment.data === null && !actions.requireSubmission) finish();
  }, [payment.isSuccess, payment.data, finish, actions.requireSubmission]);
  if (completed)
    return (
      <p className="leading-[1.65]" role="status">
        Updating billing…
      </p>
    );
  if (payment.isPending) return <Loading label="Loading payment form…" />;
  if (payment.error)
    return (
      <div>
        <ErrorNotice error={payment.error} />
        <Button onClick={() => void payment.refetch()}>Try again</Button>
      </div>
    );
  if (!payment.data)
    return actions.requireSubmission ? (
      <Button variant="primary" disabled={actions.disabled} onClick={finish}>
        {actions.submitLabel}
      </Button>
    ) : (
      <Loading label="Updating billing…" />
    );
  return (
    <PaymentForm key={payment.data.clientSecret} payment={payment.data} onComplete={finish} {...actions} />
  );
}

function PaymentForm({
  payment,
  onComplete,
  ...actions
}: { payment: Payment; onComplete: () => void } & PaymentActions) {
  const stripe = useQuery({
    queryKey: ['stripe-js', payment.publishableKey],
    queryFn: async () => {
      const instance = await loadStripe(payment.publishableKey);
      if (!instance) throw new Error('Could not load Stripe. Please try again.');
      return instance;
    },
    staleTime: Infinity,
    structuralSharing: false,
    retry: false,
  });
  if (stripe.isPending) return <Loading label="Loading payment form…" />;
  if (stripe.error)
    return (
      <div>
        <ErrorNotice error={stripe.error} />
        <Button onClick={() => void stripe.refetch()}>Try again</Button>
      </div>
    );
  return (
    <Elements
      stripe={stripe.data!}
      options={{ clientSecret: payment.clientSecret, appearance: { theme: 'stripe' } }}
    >
      <PaymentDetails payment={payment} onComplete={onComplete} {...actions} />
    </Elements>
  );
}

function PaymentDetails({
  payment,
  onComplete,
  submitLabel,
  disabled,
  onPendingChange,
}: { payment: Payment; onComplete: () => void } & PaymentActions) {
  const stripe = useStripe();
  const elements = useElements();
  const [pending, setPending] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<Error>();
  const label =
    payment.kind === 'Setup'
      ? 'Save payment method'
      : `Pay ${new Intl.NumberFormat(undefined, { style: 'currency', currency: payment.currency }).format(payment.amountDue / 100)}`;
  return (
    <form
      className="stack grid gap-5 content-start w-full max-w-140"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises -- Payment failures are caught and displayed, and pending state is reset in finally.
      onSubmit={async (event) => {
        event.preventDefault();
        if (!stripe || !elements || pending || disabled) return;
        setPending(true);
        onPendingChange?.(true);
        setError(undefined);
        try {
          const options = {
            elements,
            redirect: 'if_required' as const,
            confirmParams: { return_url: location.href },
          };
          const result =
            payment.kind === 'Setup'
              ? await stripe.confirmSetup(options)
              : await stripe.confirmPayment(options);
          if (result.error) setError(new Error(result.error.message ?? 'Payment failed. Please try again.'));
          else onComplete();
        } catch (error) {
          setError(error instanceof Error ? error : new Error('Payment failed. Please try again.'));
        } finally {
          setPending(false);
          onPendingChange?.(false);
        }
      }}
    >
      <PaymentElement
        onReady={() => setReady(true)}
        onChange={() => setError(undefined)}
        onLoadError={({ error }) => setError(new Error(error.message ?? 'Could not load the payment form.'))}
      />
      <ErrorNotice error={error} />
      <Button type="submit" variant="primary" disabled={disabled || !stripe || !ready || pending}>
        {pending ? 'Processing…' : (submitLabel ?? label)}
      </Button>
    </form>
  );
}
